import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../config/supabase';
import { messagesService } from '../services/messages';
import { useUser } from './UserContext';
import { createLogger } from '../utils/logger';

const logger = createLogger('UnreadCountContext');

interface UnreadCountContextValue {
  unreadCount: number;
}

const UnreadCountContext = createContext<UnreadCountContextValue | undefined>(undefined);

interface UnreadCountProviderProps {
  children: ReactNode;
}

export const UnreadCountProvider: React.FC<UnreadCountProviderProps> = ({ children }) => {
  const { user } = useUser();
  const [unreadCount, setUnreadCount] = useState(0);

  // Load initial count when user changes
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    const loadInitialCount = async () => {
      try {
        const count = await messagesService.getTotalUnreadCount();
        setUnreadCount(count);
      } catch (error) {
        logger.error('Failed to fetch initial count:', error);
        setUnreadCount(0);
      }
    };

    loadInitialCount();
  }, [user]);

  // Subscribe to realtime message changes
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('unread-messages-global')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
        },
        async () => {
          try {
            const count = await messagesService.getTotalUnreadCount();
            setUnreadCount(count);
          } catch (error) {
            logger.error('Failed to refresh count:', error);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return (
    <UnreadCountContext.Provider value={{ unreadCount }}>
      {children}
    </UnreadCountContext.Provider>
  );
};

export const useUnreadCountContext = (): UnreadCountContextValue => {
  const context = useContext(UnreadCountContext);
  if (context === undefined) {
    throw new Error('useUnreadCountContext must be used within an UnreadCountProvider');
  }
  return context;
};
